// ─────────────────────────────────────────────────────────────
// EMERGENT BUSINESS SOFTWARE INDEX, report content (final copy)
// Figures locked against Redash pulls and structured operator interviews.
// ─────────────────────────────────────────────────────────────

export const meta = {
  chapterNo: "01",
  chapterName: "THE INDEX",
  title: ["The age of", "custom software"],
  readingTime: "18 min",
};

export const navSections = [
  { id: "overview", label: "The shift" },
  { id: "problem", label: "Three failures" },
  { id: "workaround", label: "How they coped" },
  { id: "blocked", label: "Out of reach" },
  { id: "builders", label: "The solution" },
  { id: "operations", label: "Running on it" },
  { id: "unlocked", label: "The payoff" },
  { id: "stories", label: "The businesses" },
  { id: "methodology", label: "Methodology" },
];

export const heroStatement =
  "Businesses have always adapted to the software available to them. Now, they are adapting the software to themselves.";

export const heroIntroParas = [
  "We focus only on deployed apps because a live product is evidence. It tells us a business trusted what it built enough to run on it.",
  "We then spoke to the operators behind these apps. Different industries. Different businesses. But their stories almost always began the same way: something in the business had quietly stopped working.",
];

export const heroStats = [
  { value: 50, suffix: "K+", label: "sampled, live apps", sub: "The report's working universe: a sample, not the whole." },
  { value: 28, suffix: "+", label: "industries represented", sub: "From healthcare and retail to poultry farming." },
  { value: 8, suffix: "M+", label: "traffic on sampled apps", sub: "Real customers, clients, and staff." },
];

export const industryTicker = [
  "Retail & E-Commerce",
  "Consumer & Personal Apps",
  "Media & Entertainment",
  "Education & EdTech",
  "Professional Services",
  "Fintech & Web3",
  "Healthcare & Medical",
  "Marketing & AdTech",
  "Gaming",
  "Sports & Fitness",
  "Food & Beverage",
  "Hospitality & Travel",
  "Finance (Traditional)",
  "Construction & Engineering",
  "HR & Talent",
  "Beauty & Fashion",
  "Software & Developer Tools",
  "Real Estate & PropTech",
  "Automotive",
  "AI & Automation",
  "Logistics & Supply Chain",
  "Manufacturing & Industrial",
  "Legal & Compliance",
  "Non-Profit & NGO",
  "Agriculture & Energy",
  "Government & Public Sector",
  "Pet Care & Veterinary",
  "Insurance",
];

export const problems = [
  {
    n: "A",
    title: "Efficiency was leaking into manual work.",
    body: "Businesses kept running, but on manual effort that grew with every customer.",
    eyebrow: "WHAT IT LOOKED LIKE:",
    examples: [
      "An exam-coaching center managed 4,000 student records by hand",
      "A booking agency entered jobs for 70 contractors one at a time",
    ],
    theme: "dark",
    highlight: "manual work.",
    accent: "#12C24E",
    img: "/assets/failure-a.webp",
    next: "Next failure: software that cost more than it returned",
  },
  {
    n: "B",
    title: "The software was costing more than it returned.",
    body: "The money was leaving anyway, in fees, cuts, and subscriptions that never quite closed the gap.",
    eyebrow: "WHAT IT LOOKED LIKE:",
    examples: [
      "Home sellers hand agents $20,000-$50,000 in commission to do little more than post a listing",
      "Monthly payments for tools that fit halfway",
      "A cut to a middleman sitting between them and their own customers",
    ],
    theme: "light",
    highlight: "costing",
    accent: "#05BB44",
    img: "/assets/failure-b.webp",
    next: "Next failure: software that did not exist",
  },
  {
    n: "C",
    title: "The software they needed simply did not exist.",
    body: "Their problem was too specific, their market too small, or their idea too new for any vendor to have built a product for it. So they ran without one.",
    eyebrow: "WHAT IT LOOKED LIKE:",
    examples: [
      "A problem too specific for any vendor to chase",
      "A market too small to justify a product",
      "An idea too new for anything on the shelf",
    ],
    theme: "dark",
    highlight: "exist.",
    accent: "#12C24E",
    img: "/assets/failure-c.webp",
    next: "Next: how they coped",
  },
];

export const copingChart = {
  title: "How operators coped before they built",
  source: "Source: 300 structured operator interviews",
  labels: ["No approach that solved it", "Manual processes that buckled as they grew", "SaaS that never quite fit", "Custom builds that drained money they could not spare"],
  values: [33, 27, 27, 12],
  widths: [73.7, 51.6, 39.9, 56.2],
};

export const blockedChart = {
  title: "Share of operators citing each blocker",
  subtitle: "Of the 300 operators interviewed. Many hit more than one wall, so shares add past 100%.",
  source: "Source: 300 structured operator interviews",
  labels: ["Prohibitive development cost", "Timelines from several months to more than a year", "Tried other approaches, from no-code tools to freelancers, and hit a wall"],
  values: [43, 37, 35],
};

export const blockedCopy = [
  "A real fix meant building custom software. For most operators, that door was effectively closed. The quotes alone ended most conversations.",
  "Even when the money was there, the calendar was not, with builds running from a few months to well over a year while the problem kept costing them daily. And most of these operators had never written a line of code, so building it themselves was never a real fallback.",
];

export const blockedTakeaway =
  "The choice came down to build it or live without it, and for years living without it usually won.";

export const buildersIntro = [
  "The people who get to build changed. These systems are now built by the operators themselves: people with no coding background and no developer to hire, who a year earlier had no way to build anything at all. Building stopped being reserved for those who could write code or afford someone who could, and moved to the person who runs the business and knows exactly what it needs.",
  "What they built runs both sides of the business.",
];

export const buildersSides = [
  {
    label: "FACING THE CUSTOMER",
    title: "Storefronts, marketplaces, booking pages",
    body: "The sites that bring leads in.",
  },
  {
    label: "BEHIND THE COUNTER",
    title: "CRM, inventory, client portals, internal tools",
    body: "The machinery no vendor ships in the shape a specific business needs.",
  },
];

export const buildersOutro =
  "The portraits below cover the industries building at the highest volume. Two operator prompts each, in their own words, show the range within a single vertical: find yours, and see what people in it actually built.";

export const industries = [
  {
    num: "01", id: "ind-healthcare", name: "Healthcare & medical",
    tagline: "What a practice runs its day on",
    intro: "Healthcare operators build for how their own practice runs, and no two are alike. A solo physiotherapist needs something different from a multi-site clinic group, and both build the operating layer that off-the-shelf systems never fit. The spread runs from a physiotherapy center CRM and a clinic management SaaS to a clinical-trial intake portal and a hospital time-and-attendance system.",
    quotes: [
      { text: "Build an enterprise clinic management SaaS … multi-clinic support with patients, doctors, and rooms per location … appointment scheduling with a calendar, reminders, and statuses … clinical records, prescriptions, and treatment plans, including dental charting … billing and an admin dashboard of appointments, occupancy, and revenue per clinic.", who: "Clinic group", where: "Asia" },
      { text: "Build a clinical-trial intake and forms portal … a library of trial forms (screening, consent, follow-up) that staff can configure … a participant submission flow … a staff queue showing submitted forms by trial and status … a dashboard tracking enrolment, pending forms, and completion rates per trial.", who: "Research clinic", where: "North America" },
    ],
    builtIn: ["Role-based access", "Appointment calendars feeding occupancy & revenue", "Per-patient clinical histories", "Billing tied to completed visits", "Multi-site switching"],
  },
  {
    num: "02", id: "ind-professional", name: "Professional services",
    tagline: "The system a firm's client work runs through",
    intro: "Professional services firms each build the specific system their own work runs through, because service delivery is never the same twice. Accountants, consultants, agencies, and law firms build for how clients are tracked, how work gets billed, and how documents move. The library is dense and operational, from an accounting firm client portal to a contract lifecycle system, a ledger reconciliation tool that parses uploaded PDFs, and a sign-making quoting-and-invoicing app.",
    quotes: [
      { text: "Build a website and secure client portal for an accounting and bookkeeping firm … a client login where each business uploads receipts and bank statements and sees its filing status … a bookkeeping workspace for staff to record income and expenses per client and generate a balance sheet and income statement … an admin dashboard tracking clients, pending documents, and monthly close progress.", who: "Accounting firm", where: "North America" },
      { text: "… a quote builder for sign jobs with materials, sizes, quantities, and labour that calculates a price … conversion of accepted quotes into invoices, with deposits and balance tracking … accounting recording income and expenses and showing profit per job and per month.", who: "Print and sign shop", where: "North America" },
    ],
    builtIn: ["Client logins scoped per account", "Document upload & filing-status tracking", "Quote-to-invoice conversion", "Deposit & balance tracking", "Monthly-close workflows"],
  },
  {
    num: "03", id: "ind-retail", name: "Retail & e-commerce",
    tagline: "The storefront, and the system behind it",
    intro: "Retail operators build the storefront, and then they build the system that keeps it running. Retail is the largest building industry on the platform, and plenty of what gets built is the shop window. But these operators did not stop there. Behind the storefront they built the parts of retail that break first when a business outgrows a spreadsheet: point-of-sale, inventory, and order management. The library skews hard toward that back office, from an offline retail POS and a B2B wholesale ordering portal to a small business ERP tying products, sales, and purchasing together.",
    quotes: [
      { text: "Build a stable, production-grade web-based POS and inventory system for a mixed retail store, supporting offline sales with automatic sync … a POS sell screen with product search, a cart, and checkout that works offline … automatic syncing of offline sales once a connection returns … inventory of products with stock levels updated by each sale.", who: "Mixed retail store", where: "Asia" },
      { text: "… an order intake capturing the customer, item, due date, price, and discount … a production board moving each order through stages (design, crafting, finishing, ready) with timelines … a dashboard showing orders in production, due this week, and revenue.", who: "Jewelry shop", where: "Europe" },
    ],
    builtIn: ["Offline-capable checkout with sync", "Stock decrements per sale", "Low-stock reorder lists", "Tiered wholesale pricing", "Production stage boards"],
  },
  {
    num: "04", id: "ind-finance", name: "Finance & fintech",
    tagline: "How a business moves and tracks its own money",
    intro: "Set aside the consumer trading apps and wallets, and the operator story is a business building the system that moves and tracks its own money. Invoicing, billing, reconciliation, cash flow, receivables, and payouts: the core of getting paid and knowing where the money is. The library's operator-finance prompts are among the most concrete in the whole corpus, from an SMB cash-flow and AR dashboard to a payment reconciliation-and-dunning tool and an offline invoicing app built around WhatsApp.",
    quotes: [
      { text: "Build a cash-flow and accounts-receivable dashboard for a small business … overview cards for cash on hand, receivables, payables, and runway … an invoices table with customer, amount, due date, and a Collect Payment action … a cash-flow chart with monthly and quarterly toggles and a simple forward forecast.", who: "Small business", where: "North America" },
      { text: "Build a multi-user offline invoicing app for Android with a customer database, product inventory, and WhatsApp sharing … an invoice builder that works fully offline … WhatsApp integration to send a finished invoice to a customer … a dashboard of invoices, sales, and stock that syncs when back online.", who: "Retail business", where: "Africa" },
    ],
    builtIn: ["Collect-payment actions on receivables", "Payout schedules", "Automated dunning", "Mobile-money & card acceptance", "Offline invoicing with sync"],
  },
  {
    num: "05", id: "ind-construction", name: "Construction & engineering",
    tagline: "Running the job, the crew, and the site",
    intro: "Construction and engineering is where the operator-software story is at its plainest, because almost nothing here is a side project. Operators put onto a screen the work that otherwise lives on paper, on whiteboards, and in someone's head: site management, estimation, service tracking, and fleet operations, running right into manufacturing proper. The library covers an auto workshop job manager, a PPE inspection system, and a block factory management app tracking output per batch, mould, and shift.",
    quotes: [
      { text: "Build a workshop management app for an auto repair workshop, in Arabic with right-to-left support … a job-card flow capturing the vehicle, customer, issue, and mechanic … a job board moving each job through received, in progress, and ready … parts and labour costing that builds the invoice for each job.", who: "Auto workshop", where: "Asia" },
      { text: "Build a web app to manage personal protective equipment inspections … a PPE registry recording each item with type, serial, location, and assigned worker … an inspection workflow scheduling and logging checks with pass or fail and photos … expiry and re-inspection reminders flagging items due.", who: "Industrial site", where: "Europe" },
    ],
    builtIn: ["Job-card-to-invoice flows", "Stage boards for crews & vehicles", "Parts-and-labour costing", "Inspection logging with photos", "Expiry & re-inspection reminders"],
  },
  {
    num: "06", id: "ind-hospitality", name: "Hospitality, food & travel",
    tagline: "Bookings, service, and the cost of a venue",
    intro: "Hospitality operators build for the rhythm of service and the margin underneath it. They take bookings and reservations, manage orders, run the front of house, and hold a grip on the costs that decide whether the venue makes money. The library runs the full width of the floor, from a small-hotel management SaaS and a restaurant-and-bar system to a food-cost ERP and an Umrah travel agency management app.",
    quotes: [
      { text: "Build a SaaS … for managing small and medium hotels … a room and rate manager with availability across a calendar … a reservations module to create and manage bookings with guest details, check-in, and check-out … a front-desk view of arrivals, departures, and occupancy for the day.", who: "Small hotel", where: "South America" },
      { text: "Build a food-cost ERP for the hotel and restaurant industry … a recipe and dish costing module breaking each dish into ingredients with quantities and unit costs … a menu-engineering view showing cost, price, and margin per dish … a dashboard summarizing food-cost percentage, top and bottom-margin dishes, and purchasing spend.", who: "Restaurant", where: "Europe" },
    ],
    builtIn: ["Availability calendars & live occupancy", "Reservation-to-check-out flows", "Recipe costing with live margins", "Multi-language support", "Payment capture"],
  },
  {
    num: "07", id: "ind-agriculture", name: "Agriculture & agritech",
    tagline: "Production tools for the farm floor",
    intro: "Farming operators build production tools for the exact physical reality of their work. Poultry, livestock, and aquaculture operators are about the last people you would picture building software, and they modeled flocks, sea cages, paddocks, and grades of egg, because that is what the work involves. This is a small corner of the platform, which is what makes it worth a look.",
    quotes: [
      { text: "Build an app for egg inventory and production management on poultry farms … daily production entry recording eggs collected per shed with date and grade … an inventory view of eggs on hand by grade with stock movements for sales and breakage … flock and shed tracking linking production to each shed's bird count and laying rate.", who: "Poultry farm", where: "South America" },
      { text: "Build a tool that detects missing or extra fish in sea cages by comparing observed biological feed-conversion ratio to the expected model FCR … an FCR comparison that computes observed versus expected FCR and estimates the likely fish-count difference … an alerts view flagging cages where the count appears off.", who: "Aquaculture operation", where: "Europe" },
    ],
    builtIn: ["Production entry tied to physical units", "Grade-level inventory", "Offline capture that syncs later", "Biological-model analytics"],
  },
  {
    num: "08", id: "ind-realestate", name: "Real estate & proptech",
    tagline: "A portfolio turned into a system",
    intro: "Real estate operators turn a portfolio into a system, and the most common build is the operating layer, not the listing page. A portfolio is a set of moving parts (units, tenants, leases, rent, maintenance, viewings) that no generic tool tracks the way the operator actually works. The single largest use case in the vertical, at about a quarter of builds, is a property management system. The library splits into three operator types, each building a different spine: landlords, agencies, and specialists.",
    quotes: [
      { text: "Build a rental property management app for landlords … a portfolio view with units, tenants, rent amount, and lease status across multiple properties … a tenant portal to pay rent online with autopay, receipts, and stored lease documents … maintenance requests with status and a paid work-order flow … an owner dashboard charting collected rent, arrears with late fees.", who: "Landlord", where: "North America" },
      { text: "Build a CRM for a real-estate intermediary to record owners and tenants, replacing scattered WhatsApp chats … a property list linking each unit to its owner, status (available, reserved, rented), and interested tenants … a deal tracker moving enquiries through stages with reminders and notes per contact.", who: "Real-estate agency", where: "North America" },
    ],
    builtIn: ["Tenant portals with autopay", "Lease-document storage", "Maintenance work-order flows", "Unit-status pipelines", "Per-unit subscription billing"],
  },
];

export const operationsIntro = [
  "These are not prototypes. They generate revenue and sit at the operational core of the business. Most are not single-purpose tools either. They bring together the services a business needs into one working system.",
  "Every app sits on a database by default, so the question is what operators wired in on top. Looking across the apps they built, most wired in something real.",
];

export const integrationChart = {
  title: "How many integrations operators wired in",
  subtitle: "Cumulative shares of deployed apps. The default database that ships with every app is excluded.",
  source: "Source: Emergent platform data, deployed-app cohort",
  rows: [
    { value: 59, label: "added at least one integration beyond the database" },
    { value: 32, label: "added two or more: a payment rail, an email or messaging service, a maps or storage layer, composed into one system" },
  ],
};

export const integrationBrands = [
  { name: "Stripe", slug: "stripe" },
  { name: "Razorpay", slug: "razorpay" },
  { name: "PayPal", slug: "paypal" },
  { name: "MongoDB", slug: "mongodb" },
  { name: "Supabase", slug: "supabase" },
  { name: "WhatsApp", slug: "whatsapp" },
  { name: "Resend", slug: "resend" },
  { name: "SuprSend", slug: null, icon: "https://www.google.com/s2/favicons?domain=suprsend.com&sz=64" },
  { name: "Google Maps", slug: "googlemaps" },
  { name: "Anthropic", slug: "anthropic" },
  { name: "Twilio", slug: null, icon: "https://www.google.com/s2/favicons?domain=twilio.com&sz=64" },
  { name: "Gemini", slug: "googlegemini" },
  { name: "ElevenLabs", slug: "elevenlabs" },
];

export const moneySignals = [
  { value: 25, fraction: "1 in 4", label: "take payment", sub: "The app generates revenue directly.", unit: "25% OF DEPLOYED APPS" },
  { value: 33, fraction: "1 in 3", label: "send transactional email", sub: "Receipts, confirmations: the messages a business sends without anyone touching them.", unit: "33% OF DEPLOYED APPS" },
];

export const behaviorSignals = [
  { value: 33, fraction: "1 in 3", label: "run on a custom domain", sub: "Pointed at real customers.", unit: "33% OF DEPLOYED APPS" },
  { value: 39, fraction: "2 in 5", label: "kept building after launch", sub: "Came back to keep building a month after launch.", unit: "39% OF OPERATORS" },
];

export const unlockedChart = {
  title: "The biggest unlock, by operator",
  source: "Source: 300 structured operator interviews",
  labels: ["Nothing was possible before", "Dropped a SaaS subscription", "Retired a manual process", "Independence from developers"],
  values: [33, 33, 21, 13],
  widths: [80, 80, 43.2, 61.1],
};

export const unlockedCopy = [
  "because nothing had been possible before. It exists because they built it, or it does not exist at all. For the rest, it replaced a cost they were already carrying:",
];

export const stories = [
  {
    metric: "$60K", metricLabel: "monthly revenue, from zero",
    name: "Augustine Kwaku Agyekum", business: "A toxicology consultancy", location: "Santa Monica, California",
    who: "Augustine Kwaku Agyekum is a PhD toxicologist who advised cosmetic brands on product safety for a decade before starting his own firm.",
    problem: "Every safety assessment ran through his own hands, one formula at a time. The firm could only grow as far as his hours allowed.",
    built: "A high-throughput assessment tool on Emergent that runs formulas against his toxicology framework, plus a rebuilt firm site.",
    impact: "Inquiries at five times the prior rate, revenue near $60,000 a month from nothing since April, and the tool now reaches 174 countries.",
  },
  {
    metric: "$2M", metricLabel: "revenue, up from $900K",
    name: "Joey Baughman", business: "A roofing company", location: "Northeast Ohio",
    who: "Joey Baughman runs a five-person roofing firm that grew from $900K to $2M in revenue.",
    problem: "Five subscriptions costing up to $1,800 a month that barely spoke to each other.",
    built: "One command center on Emergent: leads, job cards, a pricing calculator that generates quotes from uploaded measurements, proposals with e-signatures, and automated customer updates.",
    impact: "The subscriptions are gone, and every job now runs through a single system.",
  },
  {
    metric: "60%", metricLabel: "fewer missed customer queries",
    name: "Sushen Dang", business: "A meal-prep business", location: "Toronto",
    who: "Sushen Dang runs meal-prep kitchens whose brands generate $100,000+ in monthly revenue.",
    problem: "A $200,000 quote for the custom system his kitchens needed, and about $2,500 a month going to fragmented SaaS tools in the meantime.",
    built: "The operating system his kitchens run on, built on Emergent for around $10,000.",
    impact: "The SaaS stack is gone, and inbound customer-service queries getting missed fell 60%.",
  },
  {
    metric: "+35%", metricLabel: "lead-form submissions in a month",
    name: "Martin Raynov", business: "A car detailing service", location: "South Florida",
    who: "Martin Raynov runs a detailing service whose website is the business's only lead channel.",
    problem: "A WordPress site that underperformed and was slow to change.",
    built: "A replacement site on Emergent, live in about four days.",
    impact: "Around 50 conversion-focused visitors a day through local search, and lead-form submissions up 35% within a month.",
  },
  {
    metric: "400%", metricLabel: "annual growth",
    name: "Andrew Clarke", business: "A delivery fleet, Drop24", location: "Johannesburg",
    who: "Andrew Clarke runs Drop24, a delivery fleet business.",
    problem: "Driver coordination lived on WhatsApp and paper.",
    built: "A fleet management app on Emergent covering maintenance, fuel, and driver behavior through a five-star rating system.",
    impact: "The business has grown 400% annually on top of it.",
  },
  {
    metric: "One portal", metricLabel: "for the entire client experience",
    name: "Alex Moore", business: "A wedding videography business", location: "Essex",
    who: "Alex Moore runs a small wedding videography studio.",
    problem: "Client bookings, payments, and final galleries had no single place couples could access on their own.",
    built: "A client portal on Emergent where couples log in to view their booking, track payments, and access their photo and video gallery.",
    impact: "A small operator now runs the client experience of a much larger studio.",
  },
  {
    metric: "<$2K", metricLabel: "vs. $40,000 agency quotes",
    name: "John Campetella", business: "A medical management company", location: "New York",
    who: "John Campetella runs a medical management company and teaches clinical sciences.",
    problem: "Agency quotes of around $40,000 for the clinical systems and patient portals his practice needed.",
    built: "A clinical intelligence system and custom patient portals on Emergent, for under $2,000.",
    impact: "In use with 400-500 patients, and rolling out with a 600-franchise gym partner.",
  },
  {
    metric: "6,800", metricLabel: "trips a month, fully traceable",
    name: "Mihir Mohan & Shiv Soni", business: "Autoverse Mobility", location: "",
    who: "Mihir Mohan and Shiv Soni run Autoverse Mobility, an 82-person business delivering genuine car parts to independent garages on a 12-vehicle EV fleet, promising 45–90 minute delivery.",
    problem: "Nothing tracked what happened between order and delivery — no fleet visibility, untimed handovers, and no trail when parts went missing; building it in-house was quoted at nine to ten months.",
    built: "A suite of five interconnected apps on Emergent — driver, warehouse, vendor pickup, admin control center, attendance — built in two and a half months and used daily by roughly half the company.",
    impact: "Delivery times halved, revenue from their top 50 garages up 35% within a month of going live, and 6,800 trips a month now fully traceable.",
  },
  {
    metric: "4+ years", metricLabel: "of projected build time, done in one quarter",
    name: "Balaji Vijayaraghavan", business: "A GTM consultancy", location: "Tamil Nadu",
    who: "Balaji Vijayaraghavan spent a decade running go-to-market strategy — 49 launches led, a proprietary framework, a book codifying it.",
    problem: "Productizing the methodology consumed an 18-person team, two and a half years, and ₹1.5–2 crore, with the roadmap still stretching to 2027–28.",
    built: "The remaining scope — a GTM strategy builder and a market research report generator — completed on Emergent in three to four months.",
    impact: "Four-plus years of projected build time became one quarter, and 100 signups, some paid, arrived before any marketing effort.",
  },
];

export const observed = {
  title: "What this index observed",
  body: "The software running these 50,000+ apps was not commissioned, purchased, or outsourced. It was built by the people who needed it, in the shape their business actually works, most of it by operators who had never built software before. For one in three of them, it could not have existed at any price they could pay. Now it takes bookings, moves inventory, collects rent, and sends receipts, every day, for businesses that stopped waiting for someone else to build it.",
  sources: "Sources: Emergent platform data (deployed-app cohort) and 300 structured operator interviews. Prompts are operator-verbatim, lightly cleaned for filler, and anonymized by business. Exact bases, query-level detail, and the measurement basis for end-user figures live in the methodology appendix.",
};

export const methodologyItems = [
  {
    label: "The universe",
    body: "This report studies businesses on Emergent with at least one deployed, live app: a sample set of 50,000+ apps as of the report snapshot. Deployment is the inclusion bar throughout. Apps that were built but never deployed are excluded from every figure.",
  },
  {
    label: "Platform figures",
    body: "All behavioral figures (custom domains, payments, transactional email, integrations, return-to-build) come from Emergent platform data. Each figure is computed on its own measurement base rather than forced onto a single denominator. Custom domain, payment, and transactional email shares are computed on the deployed-app universe. Integration counts are computed per app across apps built on the platform, excluding the default database that ships with every app; counting it would inflate every figure. The full distribution: ~59% added at least one integration, ~32% two or more, ~16% three or more, ~6% four or more. Return-to-build (39%) is computed per operator: the share still actively building on an app one month after its launch. The real estate use-case share (about a quarter of builds) is computed from platform build data, not from interviews or prompts.",
  },
  {
    label: "End users",
    body: "The eight million figure counts people using the sampled deployed apps: customers, clients, and staff on the other side of the software. It is measured on this sample only and is a floor, not a platform total.",
  },
  {
    label: "Operator interviews",
    body: "300 structured interviews with operators from the deployed cohort. Interview findings describe this group and are not extrapolated to the full population. The coping distribution (33% / 27% / 27% / 12%) is computed among the operators who were not already well served by existing software, roughly six in seven of those interviewed. Development cost figures are quotes these operators reported receiving; $20,000 is the median, not the mean.",
  },
  {
    label: "Prompts",
    body: "The prompts in the industry portraits are operator-verbatim, lightly cleaned for filler, and anonymized by business. They illustrate language and range within a vertical. No proportion claim in this report is drawn from prompts.",
  },
  {
    label: "Operator stories",
    body: "Published with operator consent. Figures in stories are as reported by the operators themselves and are attributed to them, not to platform data.",
  },
  {
    label: "Rounding",
    body: "All figures are rounded. Shares are decimal-free by design; precision beyond a whole percentage point would overstate the resolution of the underlying data.",
  },
];

export const reading = [
  { title: "\"Emergent Is Now a Unicorn: We Raised $130M in Our Series C at a $1.5B Valuation\", Emergent News", url: "https://emergent.sh/news/emergent-now-a-unicorn-at-1-5-billion-valuation", host: "emergent.sh/news" },
  { title: "\"How a Global Industrial Equipment Manufacturer Replaced a Six-Figure CRM Contract with a Tool They Built Themselves\", Case Study", url: "https://emergent.sh/case-studies/how-a-global-industrial-equipment-manufacturer-built-crm-with-emergent", host: "emergent.sh/case-studies" },
  { title: "\"How the CEO of an Event Agency Built His Own Operations Platform for €1,000\", Case Study", url: "https://emergent.sh/case-studies/how-an-event-agency-ceo-built-his-own-operations-platform", host: "emergent.sh/case-studies" },
  { title: "\"Construction Contractor Built a Crew Dispatch and Scheduling Tool to Coordinate Jobsite Assignments\", Case Study", url: "https://emergent.sh/case-studies/construction-contractor-built-crew-dispatch-scheduling-tool", host: "emergent.sh/case-studies" },
];
